// The page's side of the import worker. One worker per uploaded file, kept alive
// while the user maps columns and terminated when they finish, start again, or a
// request times out.

import type { CanonicalRota, IsoDate } from '../model/types';
import type { Report } from '../report/buildReport';
import type { Details } from './details';
import type { OpenResponse, TableInfo, WorkerRequest, WorkerResponse } from '../worker/protocol';
import type { ImportSettings } from './normalise';
import { MAX_FILE_BYTES, parseError } from './parse/types';
import type { NormaliseSummary } from './summary';

/** Distributes Omit over the request union. */
type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never;

/** How long any one worker request may run before the worker is terminated. */
export const REQUEST_TIMEOUT_MS = 20_000;

/** The subset of Worker we use, so tests can supply a fake. */
export interface WorkerLike {
  onmessage: ((event: MessageEvent<WorkerResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(message: WorkerRequest, transfer?: Transferable[]): void;
  terminate(): void;
}

export interface SessionOptions {
  sheetName?: string;
  timeoutMs?: number;
  createWorker?: () => WorkerLike;
}

export type OpenResult =
  | Extract<OpenResponse, { ok: false }>
  | {
      ok: true;
      session: ImportSession;
      table: TableInfo;
      headers: string[];
      settings: ImportSettings;
      summary: NormaliseSummary;
      vendorName?: string;
    };

const defaultWorker = (): WorkerLike =>
  new Worker(new URL('../worker/import.worker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as WorkerLike;

export class WorkerFailed extends Error {
  constructor(readonly reason: 'timeout' | 'crashed') {
    super(reason === 'timeout' ? 'The import took too long.' : 'The import worker crashed.');
  }
}

export class ImportSession {
  private nextId = 1;
  private pending = new Map<
    number,
    {
      resolve: (r: WorkerResponse) => void;
      reject: (e: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  private closed = false;

  private constructor(
    private readonly worker: WorkerLike,
    private readonly timeoutMs: number,
  ) {
    worker.onmessage = (event) => {
      const entry = this.pending.get(event.data.id);
      if (!entry) return;
      clearTimeout(entry.timer);
      this.pending.delete(event.data.id);
      entry.resolve(event.data);
    };
    worker.onerror = () => this.fail(new WorkerFailed('crashed'));
  }

  /** Reads a file in a fresh worker and makes a first guess at the columns. Never rejects. */
  static async open(file: File, options: SessionOptions = {}): Promise<OpenResult> {
    // Check the size before reading anything into memory.
    if (file.size > MAX_FILE_BYTES)
      return parseError('too-large') as Extract<OpenResponse, { ok: false }>;
    if (file.size === 0) return parseError('empty') as Extract<OpenResponse, { ok: false }>;

    const bytes = await file.arrayBuffer();
    const session = new ImportSession(
      (options.createWorker ?? defaultWorker)(),
      options.timeoutMs ?? REQUEST_TIMEOUT_MS,
    );
    try {
      const response = await session.request(
        {
          type: 'open',
          fileName: file.name,
          bytes,
          ...(options.sheetName !== undefined && { sheetName: options.sheetName }),
        },
        [bytes],
      );
      if (response.type !== 'open') throw new Error('Unexpected worker response');
      if (!response.result.ok) {
        session.close();
        return response.result;
      }
      return { ...response.result, session };
    } catch (error) {
      session.close();
      const reason = error instanceof WorkerFailed ? error.reason : 'crashed';
      return parseError(reason === 'timeout' ? 'timeout' : 'unreadable') as Extract<
        OpenResponse,
        { ok: false }
      >;
    }
  }

  /** Re-runs the import with new column choices. Rejects with WorkerFailed on timeout or crash. */
  async normalise(settings: ImportSettings): Promise<NormaliseSummary> {
    const response = await this.request({ type: 'normalise', settings });
    if (response.type !== 'normalise') throw new Error('Unexpected worker response');
    return response.summary;
  }

  /** The full rota for the latest settings. */
  async rota(): Promise<CanonicalRota | null> {
    const response = await this.request({ type: 'rota' });
    if (response.type !== 'rota') throw new Error('Unexpected worker response');
    return response.rota;
  }

  /** The planned shifts as a chart, one per day, as PDF bytes (T29). */
  async rotaChart(): Promise<ArrayBuffer | null> {
    const response = await this.request({ type: 'rotaChart' });
    if (response.type !== 'rotaChart') throw new Error('Unexpected worker response');
    return response.pdf;
  }

  /** Applies the "anything we should know?" answers, runs every rule and builds the report. */
  async check(details: Details, today: IsoDate): Promise<Report | null> {
    const response = await this.request({ type: 'check', details, today });
    if (response.type !== 'check') throw new Error('Unexpected worker response');
    return response.report;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.worker.terminate();
    for (const { reject, timer } of this.pending.values()) {
      clearTimeout(timer);
      reject(new WorkerFailed('crashed'));
    }
    this.pending.clear();
  }

  private request(
    message: WithoutId<WorkerRequest>,
    transfer: Transferable[] = [],
  ): Promise<WorkerResponse> {
    if (this.closed) return Promise.reject(new WorkerFailed('crashed'));
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => this.fail(new WorkerFailed('timeout')), this.timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      this.worker.postMessage({ ...message, id } as WorkerRequest, transfer);
    });
  }

  private fail(error: WorkerFailed): void {
    const pending = [...this.pending.values()];
    this.pending.clear();
    this.closed = true;
    this.worker.terminate();
    for (const { reject, timer } of pending) {
      clearTimeout(timer);
      reject(error);
    }
  }
}
